import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { useMount, useUnmount } from 'react-use';
import { Button, Box, Flex, Icon, Text, Hide } from '@qga/roo-ui/components';
import { getIsExclusive, getPropertyId, getIsLoading } from 'store/property/propertySelectors';
import Markup from 'components/Markup';
import filter from 'lodash/filter';
import Truncate from 'components/Truncate';
import InclusionsList from './components/InclusionsList';
import ExclusiveOffer from './components/ExclusiveOffer';
import {
  ParagraphWrapper,
  CollapsedBox,
  RoomContainer,
  OfferWrapper,
  RetrySearchLink,
  FullWidthButton,
  CollapseWrapper,
  ExclusiveHeading,
  ExtraFlex,
  TitleWrapper,
  MobileButton,
} from './CheckAvailability.style';
import OfferCheckoutLink from '../Offer/components/OfferCheckoutLink';
import {
  getAllAvailableOffers,
  getExclusiveOfferAvailabilityQuery,
  getPropertyInclusionSets,
} from 'store/exclusiveOffer/exclusiveOfferSelectors';
import isEmpty from 'lodash/isEmpty';
import { differenceInCalendarDays } from 'date-fns';
import pluralize from 'pluralize';
import { getIsPointsPay } from 'store/ui/uiSelectors';
import OfferPointsPaySlider from '../Offer/components/OfferPointsPaySlider';
import CancellationRefundModal from 'components/CancellationRefundModal';
import CancellationTooltip from 'components/CancellationTooltip';
import PromotionalSashNew from 'components/PromotionalSashNew';
import { useRouter } from 'next/router';
import usePointsConverters from 'hooks/points/usePointsConverters';
import { clearPointsBurnLuxeTiers, fetchPointsBurnLuxeTiers } from 'store/pointsBurnLuxe/pointsBurnLuxeActions';
import noop from 'lodash/noop';
import { inheritedInclusions } from './../../../../primitives';
import RoomOfferDetails from 'components/PropertyPage/PropertyAvailability/RoomTypes/RoomTypeList/Offer/components/RoomOfferDetails';
import { useBreakpoints } from 'hooks/useBreakpoints';
import useCtaClickEvent from 'hooks/useCtaClickEvent';

const FlexCollapseWrapper = CollapseWrapper.withComponent(Flex);

const AVAILABILITY_STATES = {
  POINTS_PLUS_PAY: 'points_plus_pay',
  CHECK_AVAILABILITY: 'check_availability',
  NO_AVAILABILITY: 'no_availability',
  BOOK_NOW: 'book_now',
};

const CheckAvailability = ({ room, offer, index = 0 }) => {
  const roomId = room.id;
  const offerId = offer?.id ?? room.offerId;
  const offerInstanceId = offer?.pointsTierInstanceId;
  const title = offer?.title ?? room.title;
  const description = offer?.description ?? room.description;

  const offerInclusions = offer?.inclusions ?? room.inclusions;
  const propertyInclusionSets = useSelector(getPropertyInclusionSets);
  const offerInclusionSetId = offer?.inclusionSetId;

  const { isLessThanBreakpoint } = useBreakpoints();
  const isMobile = isLessThanBreakpoint(0);
  const windowWidth = typeof window !== 'undefined' ? window.innerWidth : 1920;
  const smallTablet = windowWidth > 750 && windowWidth < 1020;

  const travelDatesMessage = 'Enter your travel dates to get the price';
  const offerInclusionSet = () => {
    let newInclusionSet;
    filter(propertyInclusionSets, (inclusionSet) => {
      if (inclusionSet?._key === offerInclusionSetId) {
        newInclusionSet = inclusionSet;
      }
    });
    return newInclusionSet;
  };

  const hasInheritedInclusions = offerInclusionSet()?.parentInclusionSetId;
  const offerInclusionSetInclusions = offerInclusionSet()?.inclusions;
  const inclusionSetInclusions = hasInheritedInclusions
    ? [...offerInclusionSetInclusions, ...inheritedInclusions(propertyInclusionSets, offerInclusionSet(), [])]
    : offerInclusionSetInclusions;
  const inclusions = inclusionSetInclusions ?? offerInclusions;
  const hasValuedAtPrice = offer?.hasValuedAtPrice;
  const valuedAtTotal = hasValuedAtPrice ? (offer?.valuedAtTotal ?? room?.valuedAtTotal) : { amount: '0', currency: 'AUD' };
  const dispatch = useDispatch();

  useMount(() => {
    dispatch(fetchPointsBurnLuxeTiers());
  });

  const currentTiers = usePointsConverters();
  const router = useRouter();
  const propertyId = useSelector(getPropertyId);
  const isExclusive = useSelector(getIsExclusive);
  const query = useSelector(getExclusiveOfferAvailabilityQuery);

  const [expanded, setExpanded] = useState(false);
  const RoomDescription = expanded ? (ParagraphWrapper as typeof CollapsedBox) : CollapsedBox;
  const isShowingPointsPaySlider = useSelector(getIsPointsPay);
  const [availabilityState, setAvailabilityState] = useState(AVAILABILITY_STATES.CHECK_AVAILABILITY);
  const isLoading = useSelector(getIsLoading);

  const { [offerId]: offerAvailability } = useSelector(getAllAvailableOffers);
  const promotionName = offerAvailability?.promotion?.name;

  const hasAvailability = !isEmpty(offerAvailability);
  const hasSearchDates = query?.checkIn && query?.checkOut;
  const cancellationPolicy = offerAvailability?.cancellationPolicy;

  useEffect(() => {
    if (hasAvailability) {
      if (hasSearchDates && isShowingPointsPaySlider) {
        setAvailabilityState(AVAILABILITY_STATES.POINTS_PLUS_PAY);
      } else if (hasSearchDates) {
        setAvailabilityState(AVAILABILITY_STATES.BOOK_NOW);
      }
    } else if (hasSearchDates) {
      setAvailabilityState(AVAILABILITY_STATES.NO_AVAILABILITY);
    } else {
      setAvailabilityState(AVAILABILITY_STATES.CHECK_AVAILABILITY);
    }
    setExpanded(hasAvailability || index === 0);
  }, [hasAvailability, hasSearchDates, index, isShowingPointsPaySlider, setAvailabilityState]);

  useUnmount(() => {
    dispatch(clearPointsBurnLuxeTiers());
  });

  let offerPayload = {
    adults: offer?.adults ?? room?.adults ?? 0,
    kids: offer?.children ?? room?.children ?? 0,
    infants: offer?.infants ?? room?.infants ?? 0,
    numNights: offer?.minNumberOfNights ?? room?.minNumberOfNights,
    totalPrice: offer?.offerTotal ?? room?.offerTotal,
    totalDiscount: valuedAtTotal,
  };

  if (availabilityState === AVAILABILITY_STATES.BOOK_NOW || availabilityState === AVAILABILITY_STATES.POINTS_PLUS_PAY) {
    offerPayload = {
      adults: query?.adults,
      kids: query?.children,
      infants: query?.infants,
      numNights: differenceInCalendarDays(new Date(query?.checkOut), new Date(query?.checkIn)),
      totalPrice: offerAvailability?.charges?.total,
      totalDiscount: offerAvailability?.charges?.totalBeforeDiscount,
    };
  }
  const { ctaClickEvent } = useCtaClickEvent();

  const onCheckAvailabilityClicked = () => {
    ctaClickEvent({ itemText: 'Check Availability', itemType: 'button', url: '#check-availability' });
    const roomTypeId = roomId || offerId;
    if (roomTypeId) {
      router.replace(
        {
          hash: 'rooms',
          query: { ...router.query, roomTypeId },
        },
        undefined,
        { shallow: true, scroll: false },
      );
    }
  };

  const totalCash = offerAvailability?.charges?.totalCash || offerAvailability?.charges?.total || offerPayload.totalPrice;
  const totalPoints = totalCash && currentTiers.convertCashToPoints({ cash: totalCash.amount });

  const showBottomBorder = (cancellationPolicy || title) ?? false;

  return (
    <RoomContainer width={isExclusive ? ['94.85%', '744px', '100%'] : '100%'} exclusiveOffer={isExclusive}>
      <OfferWrapper
        isMobile={isMobile}
        ml={isExclusive ? 0 : [0, 0, 4]}
        mt={[0, 2, 13]}
        pt={isExclusive ? [0, 6, 8] : [0, 8]}
        pb={isExclusive ? [0, 6, 8] : [0, 8]}
        px={isExclusive ? [0, 6, 8] : [0, 8]}
      >
        <ExtraFlex
          width={isExclusive ? ['100%', '297px', '50%'] : ['100%', '50%']}
          pb={[4, 0]}
          data-testid="extra-flex"
          justify-content="flex-start"
          expanded={expanded}
          backgroundColor={!isExclusive && isMobile ? 'greys.porcelain' : 'white'}
        >
          {isExclusive ? (
            <RoomDescription isExclusive={isExclusive}>
              {promotionName && (
                <Box py={[0, 0, 0]} display={['block', 'none', 'none', 'none']}>
                  <PromotionalSashNew promotionName={promotionName} />
                </Box>
              )}
              <TitleWrapper showBottomBorder={!!showBottomBorder}>
                {!!title && (
                  <ExclusiveHeading
                    pt={!hasAvailability ? [0, 4, 0] : !promotionName ? [4, 0] : [5, 0]}
                    onClick={() => setExpanded((prev) => !prev)}
                  >
                    {title}
                  </ExclusiveHeading>
                )}

                {cancellationPolicy && (
                  <>
                    <Hide md lg>
                      <CancellationRefundModal
                        cancellationPolicy={cancellationPolicy}
                        fontSize={['sm', 'base']}
                        my={2}
                        pb={[2, 0]}
                        data-testid="check-availability-cancellation"
                      />
                    </Hide>
                    <Hide sm xs>
                      <CancellationTooltip cancellationPolicy={cancellationPolicy} />
                    </Hide>
                  </>
                )}
                <MobileButton
                  width="100%"
                  onClick={() => setExpanded((prev) => !prev)}
                  aria-label={`${expanded ? 'Collapse' : 'Expand'} Offer Details`}
                  mb={[2, 0]}
                  data-testid="mobile-toggle-button"
                >
                  <Icon name={expanded ? 'expandLess' : 'expandMore'} size={24} />
                </MobileButton>
              </TitleWrapper>

              {inclusions && <InclusionsList inclusions={inclusions} />}

              {description && (
                <Text data-testid="check-availability-room-description">
                  <strong>Offers: </strong>
                  {description}
                </Text>
              )}
            </RoomDescription>
          ) : (
            description && (
              <>
                <Flex justifyContent="space-between" alignItems="center"></Flex>
                <Truncate lines={smallTablet ? 6 : isMobile ? 3 : 10}>
                  <RoomDescription isExclusive={isExclusive}>
                    <Markup content={room.description} />
                  </RoomDescription>
                </Truncate>
                <Box mt={4}>
                  <RoomOfferDetails roomType={room} />
                </Box>
              </>
            )
          )}
        </ExtraFlex>

        <Flex
          alignItems="stretch"
          width={isExclusive ? ['100%', '319px', '50%'] : ['100%', '50%']}
          pl={isExclusive ? [4, 6, 6] : [0, 2, 6]}
          pr={isExclusive ? [4, 6, 0] : 0}
          flexDirection="column"
          pt={isExclusive ? [4, 0, 0] : 0}
          mt={promotionName ? [0, 10, 10] : 0}
          backgroundColor={!isExclusive ? ['greys.porcelain', 'white', 'white'] : 'white'}
          borderColor="greys.alto"
          borderRadius="default"
          pb={isExclusive && !hasAvailability ? [4, 0, 0] : 0}
          ml={[0, 5]}
        >
          {isExclusive ? (
            <>
              {promotionName && (
                <Box
                  position="absolute"
                  left={['15px', 'initial']}
                  top={[0, 20]}
                  mb={[4, 0, 0]}
                  display={['none', 'block', 'block', 'block']}
                >
                  <PromotionalSashNew promotionName={promotionName} />
                </Box>
              )}
              <ExclusiveOffer {...offerPayload} />
              {!isLoading && isShowingPointsPaySlider && availabilityState !== AVAILABILITY_STATES.NO_AVAILABILITY && (
                <Box mt={[0, 5]}>
                  <OfferPointsPaySlider
                    hasAvailability={availabilityState === AVAILABILITY_STATES.POINTS_PLUS_PAY}
                    onCheckAvailabilityClicked={onCheckAvailabilityClicked}
                    onCashPaymentAmountChange={noop}
                    offerName={offerAvailability?.name}
                    totalCash={totalCash}
                    totalPointsAmount={totalPoints}
                    payableAtProperty={offerAvailability?.charges?.payableAtProperty?.total ?? { amount: '0.00' }}
                    offerId={offerId}
                    offerInstanceId={offerInstanceId}
                    roomTypeId={roomId}
                    propertyId={propertyId}
                    roomName={room?.name}
                    label={
                      <Box as={Text} textAlign="left" width="100%">
                        Total for {pluralize('night', offerPayload.numNights, true)}
                      </Box>
                    }
                    exclusiveOffer={isExclusive}
                  />
                </Box>
              )}
            </>
          ) : (
            <Text fontWeight="bold" pt={1} mb={2} data-testid="check-message">
              {' '}
              {travelDatesMessage}
            </Text>
          )}

          {hasAvailability && availabilityState === AVAILABILITY_STATES.BOOK_NOW && (
            <OfferCheckoutLink
              propertyId={propertyId}
              roomTypeId={room.id}
              offerId={offerId}
              offerName={offerAvailability.name}
              roomName={room?.name}
              mt={4}
              exclusiveOffer={isExclusive}
            >
              <Text my={1} display="block" fontSize="base" fontWeight="bold">
                Book now
              </Text>
            </OfferCheckoutLink>
          )}
          {availabilityState === AVAILABILITY_STATES.NO_AVAILABILITY && (
            <>
              <Text fontSize="md" color="orange" fontWeight="bold" mb={3}>
                Not available for your current dates
              </Text>
              <RetrySearchLink href="#rooms">Please try different dates</RetrySearchLink>
            </>
          )}
          {availabilityState === AVAILABILITY_STATES.CHECK_AVAILABILITY && (!isShowingPointsPaySlider || !isExclusive) && (
            <Flex flexDirection="column" width="100%">
              <Button
                width={['100%', '90%']}
                variant="primary"
                onClick={onCheckAvailabilityClicked}
                data-testid="check-availability-cta"
                mt={isExclusive ? 5 : 0}
              >
                Check Availability
              </Button>
            </Flex>
          )}
        </Flex>
        <FlexCollapseWrapper xs alignItems="flex-start">
          <FullWidthButton
            onClick={() => setExpanded((prev) => !prev)}
            aria-label={`${expanded ? 'Collapse' : 'Expand'} Offer Details`}
            data-testid="clickable-area"
          >
            <Icon name={expanded ? 'expandLess' : 'expandMore'} size={24} mt={5} mb={100} />
          </FullWidthButton>
        </FlexCollapseWrapper>
      </OfferWrapper>
    </RoomContainer>
  );
};

CheckAvailability.propTypes = {
  index: PropTypes.number,
  room: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    offerId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    title: PropTypes.string,
    name: PropTypes.string.isRequired,
    description: PropTypes.string,
    inclusions: PropTypes.array,
    maxOccupantCount: PropTypes.number,
    minNumberOfNights: PropTypes.number,
    promotionName: PropTypes.string,
    offerTotal: PropTypes.shape({
      amount: PropTypes.string,
      currency: PropTypes.string,
    }),
    valuedAtTotal: PropTypes.shape({
      amount: PropTypes.string,
      currency: PropTypes.string,
    }),
    hasValuedAtPrice: PropTypes.bool,
    adults: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    children: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    infants: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  }).isRequired,
  offer: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    pointsTierInstanceId: PropTypes.string,
    title: PropTypes.string,
    description: PropTypes.string,
    inclusions: PropTypes.array,
    offerTotal: PropTypes.shape({
      amount: PropTypes.string,
      currency: PropTypes.string,
    }),
    hasValuedAtPrice: PropTypes.bool,
    valuedAtTotal: PropTypes.shape({
      amount: PropTypes.string,
      currency: PropTypes.string,
    }),
    minNumberOfNights: PropTypes.number,
    adults: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    children: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    infants: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    inclusionSetId: PropTypes.string,
    parentInclusionSetId: PropTypes.string,
  }),
};

export default CheckAvailability;
